import { AddWorksheetOptions, Workbook, Worksheet } from 'exceljs'
import { HourSeriesResult } from './hour-series-calculator'
import { hoursInYear } from '../util/hours-in-year'

type ColumnData = {
  index: number
  heading?: string
  type?: string
  name: string
  values: number[]
}

type AddSheetData = {
  hourSeriesData: HourSeriesResult[]
}

const addDataToSheet = (sheet: Worksheet, data: AddSheetData) => {
  const { hourSeriesData } = data

  const formatValue = (value: number) => value

  let index = 1
  const columnData = hourSeriesData.map((serie): ColumnData => {
    index += 1

    return {
      index,
      heading: serie.energySystemType.toString(),
      type: serie.hourSerieType.toString(),
      name: serie.hourSerieName,
      values: serie.hourSerie.map(formatValue),
    }
  })

  // Hack first column data
  columnData.unshift({
    index: 1,
    heading: 'EJ tyyppi',
    type: 'TS Tyyppi',
    name: 'Nimi',
    values: Array.from(Array(hoursInYear).keys()).map(
      (hourIndex) => hourIndex + 1,
    ),
  })

  // Insert column data
  columnData.forEach((filter) => {
    // eslint-disable-next-line no-param-reassign
    sheet.getColumn(filter.index).values = filter.values
  })

  const headerRows = [
    columnData.map((filter) => filter.heading),
    columnData.map((filter) => filter.type),
    columnData.map((filter) => filter.name),
  ]
  sheet.insertRows(1, headerRows)
}

/* eslint-disable no-param-reassign */
const applyStylesToSheet = (sheet: Worksheet) => {
  // Set default font.
  sheet.columns.forEach((column) => {
    column.font = {
      ...column.font,
      name: 'Calibri',
      size: 11,
    }
  })

  // Bold headers on first two rows.
  // sheet.getRows(1, 2)?.forEach((row) =>
  //   row.eachCell((cell) => {
  //     cell.style.font = {
  //       ...cell.style.font,
  //       bold: true,
  //     }
  //   }),
  // )

  // Bold additional headers in first column.
  const additionalHeaderCells = [
    sheet.getCell('A1'),
    sheet.getCell('A2'),
    sheet.getCell('A3'),
  ]
  additionalHeaderCells.forEach((cell) => {
    cell.style.font = {
      ...cell.style.font,
      bold: true,
    }
  })

  // // One decimal for single values.
  // sheet.columns.slice(1).forEach((column) => {
  //   column.numFmt = '#,##0.0'
  // })

  // // One decimal for total and max values.
  // sheet.getRows(3, 2)?.forEach((row) => {
  //   row.numFmt = '#,##0.0'
  // })

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  const pixelsPerExcelWidthUnit = 7
  const paddingInPixels = 5

  const estimateCellWidth = (location: string) => {
    const cell = sheet.getCell(location)
    const content = cell.toString()

    if (!context || !cell.font?.size || !cell.font?.name) {
      return content.length
    }

    context.font = `${cell.font.size}pt ${cell.font.name}, sans-serif`

    return (
      (context.measureText(content).width + paddingInPixels) /
      pixelsPerExcelWidthUnit
    )
  }

  // Set first column width.
  sheet.getColumn('A').width = Math.max(
    estimateCellWidth('A1'),
    estimateCellWidth('A2'),
  )

  // Set first column background color.
  sheet.getColumn('A').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {
      argb: 'FFFFEB9C',
    },
  }

  // Set column widths based on header width.
  sheet.columns.slice(1).forEach((column) => {
    column.width = estimateCellWidth(`${column.letter ?? 'A'}2`)
  })
}
/* eslint-enable no-param-reassign */

const WORKSHEET_NAME_MAX_LENGTH = 31

/** Worksheet name cannot include any of the following characters: * ? : \ / [ ] */
export const sanitizeWorksheetName = (name: string): string =>
  name.replace(/[*?:\\/[\]]/g, '').slice(0, WORKSHEET_NAME_MAX_LENGTH)

export function createHourSeriesExport(
  hourSeriesData: HourSeriesResult[],
): Workbook {
  const usedWorksheetNames = new Set<string>()

  const workbook = new Workbook()
  workbook.creator = 'Sweco ENEX'
  workbook.created = new Date()

  const getUniqueWorksheetName = (name: string): string => {
    const sanitizedName = sanitizeWorksheetName(name)

    if (!usedWorksheetNames.has(sanitizedName)) {
      return sanitizedName
    }

    for (let i = 0; i < 1000; i++) {
      const postfix = `-${i}`
      const nameSuggestion = `${sanitizedName.slice(
        0,
        WORKSHEET_NAME_MAX_LENGTH - postfix.length,
      )}${postfix}`

      if (!usedWorksheetNames.has(nameSuggestion)) {
        return nameSuggestion
      }
    }

    throw new Error(`Unable to produce unique worksheet name for ${name}`)
  }

  const options: Partial<AddWorksheetOptions> = {
    // Freeze first three rows.
    views: [{ state: 'frozen', ySplit: 3 }],
  }

  const addWorksheet = (name: string, data: AddSheetData) => {
    const uniqueName = getUniqueWorksheetName(name)

    usedWorksheetNames.add(uniqueName)

    const worksheet = workbook.addWorksheet(uniqueName, options)

    addDataToSheet(worksheet, data)

    // const nameCell = worksheet.getCell('A1')
    // nameCell.value = name

    applyStylesToSheet(worksheet)
  }

  // Hour series sheet
  addWorksheet('Tuntisarjat', { hourSeriesData })

  return workbook
}
